import { toast } from "react-toastify";

import {
  CreateOrgSecretRequestBodyType,
  CreateOrgSecretResponseBodyType,
  ListOrgSecretsResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { orgSecretKeys, shippingProviderKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

const createOrgSecret = async (
  payload: ApiRequest<CreateOrgSecretRequestBodyType>
) => {
  const resp = await api.post<
    CreateOrgSecretResponseBodyType,
    CreateOrgSecretRequestBodyType
  >("/v2/org-secrets", payload.body);
  return resp.data;
};

export const useCreateOrgSecret = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateOrgSecretResponseBodyType,
    Error,
    ApiRequest<CreateOrgSecretRequestBodyType>,
    unknown
  >({
    mutationFn: async (data) => createOrgSecret(data),
    onSuccess: async (data) => {
      queryClient.setQueryData<ListOrgSecretsResponseBodyType | undefined>(
        orgSecretKeys.all,
        (currentData) => {
          if (!currentData) return { secrets: [data.secret] };
          if (
            currentData.secrets.some((secret) => secret.id === data.secret.id)
          ) {
            return currentData;
          }
          return {
            secrets: [...currentData.secrets, data.secret].sort((left, right) =>
              left.name.localeCompare(right.name)
            ),
          };
        }
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orgSecretKeys.all }),
        queryClient.invalidateQueries({ queryKey: shippingProviderKeys.all }),
      ]);
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to create secret${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
